import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { StatusDot } from '../components/StatusDot'
import { PubBadge } from '../components/PubBadge'
import { Button } from '../components/Button'
import { Metric } from '../components/Metric'
import { ApiError, api, type EmailLogPage, type SubscriberDetail, type SubscriberOrdersResponse, type Subscription } from '../api/client'
import { fmtCardExpiry, fmtDateTime, fmtInt, fmtMoney, fmtName, fmtRelative, STATUS_TONES } from '../lib/format'
import { MemberSupportPanel } from './MemberSupportPanel'
import { COUNTRIES, US_STATES, countryCode, countryLabel, stateCode, stateLabel } from '../lib/address'
import { ARHistoryTimeline } from '../components/ARHistoryTimeline'
import { GatewayLedgerPanel } from '../components/GatewayLedgerPanel'
import { StaffViewButton } from '../components/StaffViewButton'
import { conflictsFrom, noticeSummary, type EmailConflict } from '../lib/emailChange'

export function SubscriberDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { data: sub, isLoading } = useQuery({
    queryKey: ['subscriber', id],
    queryFn: () => api.get<SubscriberDetail>(`/api/subscribers/${id}`),
    enabled: !!id,
  })

  const resync = useMutation({
    mutationFn: () =>
      api.post<SubscriberDetail & { refreshed?: { subscriptions: number } }>(
        `/api/subscribers/${id}/resync`,
      ),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['subscriber', id], fresh)
    },
  })
  const resyncError =
    resync.error instanceof ApiError
      ? (typeof resync.error.detail === 'object' && resync.error.detail !== null
          ? ((resync.error.detail as Record<string, unknown>).detail as string) ??
            resync.error.message
          : resync.error.message)
      : resync.error
        ? (resync.error as Error).message
        : null

  if (isLoading) {
    return (
      <div className="p-8">
        <span className="label text-paper-trace">Loading…</span>
      </div>
    )
  }

  if (!sub) {
    return (
      <div className="p-8">
        <span className="label text-radar-red">Not found</span>
      </div>
    )
  }

  const activeCount = sub.subscriptions.filter((s) => s.status === 'Active').length

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/subscribers" className="label-amber hover:underline">← Subscribers</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Record #{sub.simplecirc_account_id}</span>
      </div>

      <header className="border-b border-ink-500 pb-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
              {fmtName(sub.first_name, sub.last_name)}
            </h1>
            <div className="font-mono text-[13px] text-paper-dim mt-1">
              {sub.email || <span className="text-paper-trace italic">no email on file</span>}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex flex-col items-end gap-1">
              {sub.email_suppressed_at ? (
                <span className="flex items-center gap-2">
                  <StatusDot tone="err" pulse />
                  <span className="label text-radar-red">SUPPRESSED</span>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <StatusDot tone="ok" />
                  <span className="label">DELIVERABLE</span>
                </span>
              )}
              <span className="font-mono text-[10px] text-paper-trace tabular-nums">
                synced {sub.last_synced_at ? fmtRelative(sub.last_synced_at) : 'never'}
              </span>
            </div>
            <div className="flex flex-col items-end gap-1">
              <StaffViewButton subscriberId={sub.id} />
              {sub.simplecirc_resync && (
              <Button
                variant="outline"
                size="sm"
                disabled={resync.isPending}
                onClick={() => resync.mutate()}
              >
                {resync.isPending ? 'Resyncing…' : 'Resync from SimpleCirc'}
              </Button>
              )}
              {resync.isSuccess && !resync.isPending && (
                <span className="font-mono text-[10px] text-radar-green">
                  ✓ refreshed {resync.data?.refreshed?.subscriptions ?? 0} subscription(s)
                </span>
              )}
              {resyncError && (
                <span className="font-mono text-[10px] text-radar-red max-w-[320px] text-right">
                  {resyncError}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel label="Account"><div className="space-y-3">
          <KV k="SimpleCirc ID" v={sub.simplecirc_account_id} mono />
          <KV k="AAM Member ID" v={sub.member_account_number} mono />
          <KV k="Created" v={fmtDateTime(sub.created_at)} mono />
          <KV k="Last synced" v={fmtDateTime(sub.last_synced_at)} mono />
          <KV k="Suppressed" v={fmtDateTime(sub.email_suppressed_at) || 'No'} mono />
        </div></Panel>

        <ContactPanel sub={sub} />

        <Panel label="Summary" accent="amber"><div className="flex gap-4">
          <Metric label="Active" value={activeCount} tone="green" size="md" />
          <Metric label="All subs" value={sub.subscriptions.length} tone="paper" size="md" />
        </div></Panel>
      </section>

      {/* Track A · Phase A4 — auth/entitlement support panel.
          Sourced from /api/admin/members/{id} (separate from the
          /api/subscribers/{id} billing view). */}
      <MemberSupportPanel subscriberId={sub.id} />

      <Panel label="Auto-renewal history">
        <ARHistoryTimeline subscriberId={sub.id} />
      </Panel>

      {/* Deliberately adjacent to the panel above: that one is SimpleCirc's
          account of each renewal attempt, this one is the gateway's. Refunds,
          the card at charge time and held-then-expired authorizations exist
          only here. */}
      <Panel label="Gateway ledger · Authorize.Net" accent="cyan">
        <GatewayLedgerPanel subscriberId={sub.id} />
      </Panel>

      {/* "Did this member actually get their email?" — including the sends
          that were refused before Mailgun was ever called. */}
      <EmailHistoryPanel subscriberId={sub.id} />

      {/* Raw SimpleCirc data from each subscription's last CSV import */}
      <RawSimpleCircPanels subscriptions={sub.subscriptions} />

      <Panel label="Subscriptions" bodyClassName="p-0">
        {sub.subscriptions.length === 0 ? (
          <div className="px-4 py-10 text-center label text-paper-trace">No subscriptions</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Publication</Th>
                <Th>Status</Th>
                <Th>Channel</Th>
                <Th align="right">Last Paid</Th>
                <Th align="right">Months Left</Th>
                <Th>Period End</Th>
                <Th>Auto-renew</Th>
                <Th>Flags</Th>
              </tr>
            </thead>
            <tbody>
              {sub.subscriptions.map((s) => (
                <tr key={s.id} className="border-b border-ink-500/40">
                  <Td><PubBadge slug={s.publication_slug} /></Td>
                  <Td mono>
                    <span className={STATUS_TONES[s.status] ?? 'text-paper'}>{s.status}</span>
                  </Td>
                  <Td mono subtle>{s.payment_type || '—'}</Td>
                  <Td mono align="right">
                    {s.last_payment_amount ? fmtMoney(s.last_payment_amount) : '—'}
                  </Td>
                  <Td mono align="right">{s.never_expires ? 'never' : s.months_left}</Td>
                  <Td mono subtle>{fmtDateTime(s.current_period_end)}</Td>
                  <Td>
                    {s.auto_renew ? (
                      <span className="inline-flex items-center gap-2">
                        <Flag tone="amber">AUTO</Flag>
                        {s.auto_renew_status && (
                          <span className="font-mono text-[10px] text-paper-dim">
                            {s.auto_renew_status}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="label text-paper-trace">—</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      {s.do_not_renew && <Flag tone="amber">DNR</Flag>}
                      {s.never_expires && <Flag tone="cyan">NE</Flag>}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <PaymentHistoryPanel subscriberId={sub.id} />
    </div>
  )
}

function PaymentHistoryPanel({ subscriberId }: { subscriberId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriber-orders', subscriberId],
    queryFn: () => api.get<SubscriberOrdersResponse>(`/api/subscribers/${subscriberId}/orders`),
  })

  if (isLoading) {
    return (
      <Panel label="Payment history">
        <div className="label text-paper-trace p-4">Loading orders…</div>
      </Panel>
    )
  }
  if (error) {
    return (
      <Panel label="Payment history" accent="red">
        <div className="font-mono text-[11px] text-radar-red p-4">
          {error instanceof Error ? error.message : 'Failed to load orders'}
        </div>
      </Panel>
    )
  }
  if (!data || data.total_orders === 0) {
    return (
      <Panel label="Payment history">
        <div className="label text-paper-trace p-4">
          No order history recorded for this subscriber.
        </div>
      </Panel>
    )
  }

  return (
    <Panel label="Payment history" accent="green">
      <div className="space-y-4">
        <div className="flex items-baseline justify-between border-b border-ink-500/60 pb-3">
          <div>
            <div className="label text-paper-faint">Lifetime paid</div>
            <div className="flex items-baseline gap-4">
              <span className="font-mono text-[28px] font-semibold text-radar-green tabular-nums leading-none">
                {fmtMoney(data.lifetime_net, true, 2)}
              </span>
              {data.lifetime_refunded > 0 && (
                <span className="font-mono text-[11px] text-paper-dim">
                  {fmtMoney(data.lifetime_paid, true, 2)} paid − {fmtMoney(data.lifetime_refunded, true, 2)} refunded
                </span>
              )}
            </div>
          </div>
          <div className="text-right font-mono text-[11px] text-paper-dim">
            <div>
              {fmtInt(data.total_orders)} order{data.total_orders === 1 ? '' : 's'}
            </div>
            {data.first_order_date && data.last_order_date && (
              <div>
                {data.first_order_date.slice(0, 10)} → {data.last_order_date.slice(0, 10)}
              </div>
            )}
          </div>
        </div>

        {data.groups.map((g) => (
          <details open key={g.publication_slug} className="group/pg">
            <summary className="flex items-baseline justify-between cursor-pointer list-none select-none hover:bg-ink-700/20 px-2 py-1.5 -mx-2 border-b border-ink-500/30">
              <span className="flex items-center gap-3">
                {g.publication_slug.startsWith('__')
                  ? <span className="label-amber">{g.publication_name}</span>
                  : <PubBadge slug={g.publication_slug} />}
                <span className="label text-paper-trace">
                  {g.order_count} order{g.order_count === 1 ? '' : 's'}
                </span>
              </span>
              <span className="font-mono text-[13px] text-radar-green tabular-nums">
                {fmtMoney(g.subtotal_paid - g.subtotal_refunded, true, 2)}
                {g.subtotal_refunded > 0 && (
                  <span className="text-paper-trace text-[10px] ml-2">
                    ({fmtMoney(g.subtotal_refunded, true, 2)} refunded)
                  </span>
                )}
              </span>
            </summary>
            <table className="w-full mt-1">
              <thead>
                <tr className="border-b border-ink-500/30">
                  <Th>Date</Th>
                  <Th>Transaction ID</Th>
                  <Th align="right">Amount</Th>
                  <Th>Type</Th>
                  <Th>Card</Th>
                  <Th>Kind</Th>
                  <Th>Promo</Th>
                  <Th align="right">Remit</Th>
                </tr>
              </thead>
              <tbody>
                {g.orders.map((o) => (
                  <tr
                    key={o.id}
                    className={`border-b border-ink-500/20 ${o.kind === 'refund' ? 'text-paper-dim' : ''}`}
                  >
                    <Td mono subtle>{o.order_date ? o.order_date.slice(0, 10) : '—'}</Td>
                    <Td mono subtle><TransactionId id={o.simplecirc_order_id} /></Td>
                    <Td mono align="right">
                      <span
                        className={`${
                          o.kind === 'refund' ? 'line-through text-radar-red' :
                          o.paid_by_other ? 'text-paper-trace' : 'text-paper'
                        }`}
                      >
                        {o.amount_paid !== null ? fmtMoney(o.amount_paid, false, 2) : '—'}
                      </span>
                      {o.paid_by_other && (
                        <span className="block text-[10px] text-paper-trace italic leading-tight mt-0.5">
                          paid by {o.payer_full_name || 'other account'}
                        </span>
                      )}
                    </Td>
                    <Td mono subtle>{o.payment_type || '—'}</Td>
                    <Td mono subtle>
                      {o.last4 ? (
                        <>
                          <span className="text-paper-dim">
                            {o.card_type || 'card'} ····{o.last4}
                          </span>
                          {o.card_expiry && (
                            <span className="block text-[10px] text-paper-trace leading-tight">
                              exp {fmtCardExpiry(o.card_expiry)}
                            </span>
                          )}
                        </>
                      ) : '—'}
                    </Td>
                    <Td mono subtle>
                      <span className={
                        o.kind === 'refund' ? 'text-radar-red' :
                        o.kind === 'void' ? 'text-paper-trace' : 'text-paper-dim'
                      }>
                        {o.kind === 'payment' && o.new_or_renewal
                          ? `${o.kind} · ${o.new_or_renewal}`
                          : o.kind}
                      </span>
                    </Td>
                    <Td mono subtle>{o.promo_code || '—'}</Td>
                    <Td mono align="right" subtle>
                      {o.payment_remit_amount !== null && o.payment_remit_amount !== 0
                        ? fmtMoney(o.payment_remit_amount, false, 2)
                        : '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ))}
      </div>
    </Panel>
  )
}

/** The order's transaction ID — SimpleCirc order #, or the gateway's own
 *  id (Authorize.Net / PayPal / Stripe) for orders we sold. Click copies it. */
function TransactionId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false)
  if (!id) return <>—</>
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      window.prompt('copy this transaction ID', id)
    }
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      title="Copy transaction ID"
      className="font-mono text-paper-dim hover:text-paper break-all text-left"
    >
      {copied ? 'copied!' : id}
    </button>
  )
}

type IdentityForm = {
  first_name: string
  last_name: string
  email: string
  address_1: string
  address_2: string
  city: string
  state: string
  zipcode: string
  country: string
  note: string
}

const NAME_FIELDS = ['first_name', 'last_name'] as const
const ADDRESS_FIELDS = ['address_1', 'address_2', 'city', 'state', 'zipcode', 'country'] as const

function formFrom(sub: SubscriberDetail): IdentityForm {
  return {
    first_name: sub.first_name ?? '',
    last_name: sub.last_name ?? '',
    email: sub.email ?? '',
    address_1: sub.address_1 ?? '',
    address_2: sub.address_2 ?? '',
    city: sub.city ?? '',
    // US state and country as codes — what the server stores. A SimpleCirc
    // name it can't place comes through blank (lib/address).
    state: countryCode(sub.country) === 'US' ? stateCode(sub.state) || (sub.state ?? '') : (sub.state ?? ''),
    zipcode: sub.zipcode ?? '',
    country: countryCode(sub.country),
    note: '',
  }
}

/** Contact details, with staff Edit for name, address and email.

    Edit is offered only when `identity_editable` — while SimpleCirc still
    owns the record its import rewrites these fields overnight, so the
    server refuses (409) and the panel says where to edit instead. Name,
    address and email are saved separately, and only the parts that changed. */
function ContactPanel({ sub }: { sub: SubscriberDetail }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<IdentityForm>(() => formFrom(sub))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<EmailConflict[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const original = formFrom(sub)
      const note = form.note.trim() || null
      if (NAME_FIELDS.some((f) => form[f] !== original[f])) {
        await api.patch(`/api/admin/members/${sub.id}/name`, {
          first_name: form.first_name, last_name: form.last_name, note,
        })
      }
      if (ADDRESS_FIELDS.some((f) => form[f] !== original[f])) {
        // The server reads a blank country as US; a SimpleCirc misspelling
        // it couldn't place must be chosen, never defaulted.
        if (!form.country) {
          throw new ApiError(422, { detail: { errors: { country: 'choose a country' } } })
        }
        await api.patch(`/api/admin/members/${sub.id}/address`, {
          ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, form[f]])), note,
        })
      }
      if (form.email.trim().toLowerCase() !== (sub.email ?? '')) {
        const res = await api.patch<{ notices: { old: string; new: string } }>(
          `/api/admin/members/${sub.id}/email`, { email: form.email, note },
        )
        setNotice(noticeSummary(res.notices))
      }
    },
    onSuccess: () => setEditing(false),
    onError: (err) => {
      setConflicts(err instanceof ApiError ? conflictsFrom(err.detail) : [])
      const body = err instanceof ApiError
        ? (err.detail as { detail?: string | { errors?: Record<string, string>; message?: string } } | null)
        : null
      const detail = body?.detail
      if (detail && typeof detail === 'object' && detail.errors) {
        setErrors(detail.errors)
        setFailure(null)
      } else {
        setErrors({})
        setFailure(typeof detail === 'string' ? detail : (detail as { message?: string } | undefined)?.message ?? (err as Error).message)
      }
    },
    // Name may have saved before the address failed — refresh either way.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['subscriber', String(sub.id)] })
      qc.invalidateQueries({ queryKey: ['admin-member', sub.id] })
      qc.invalidateQueries({ queryKey: ['admin-member-changes', sub.id] })
    },
  })

  const startEdit = () => {
    setForm(formFrom(sub))
    setErrors({})
    setFailure(null)
    setConflicts([])
    setNotice(null)
    save.reset()
    setEditing(true)
  }

  const right = sub.identity_editable ? (
    !editing && (
      <button type="button" className="label text-paper-trace hover:text-amber-glow" onClick={startEdit}>
        edit
      </button>
    )
  ) : (
    <span className="label text-paper-trace" title="SimpleCirc's import rewrites name, address and email for this member">
      managed in SimpleCirc
    </span>
  )

  if (!editing) {
    return (
      <Panel label="Contact" right={right}><div className="space-y-3">
        <KV k="Name" v={[sub.first_name, sub.last_name].filter(Boolean).join(' ')} />
        <KV k="Email" v={sub.email} mono />
        {notice && <div className="font-mono text-[10px] text-paper-trace">{notice}</div>}
        {!sub.identity_editable && (
          <div className="font-mono text-[10px] text-paper-trace">
            SimpleCirc manages this member's email — change it there.
          </div>
        )}
        <KV k="Address 1" v={sub.address_1} />
        <KV k="Address 2" v={sub.address_2} />
        <KV k="City" v={sub.city} />
        <KV k="State" v={stateLabel(sub.state, sub.country)} />
        <KV k="ZIP" v={sub.zipcode} mono />
        <KV k="Country" v={countryLabel(sub.country)} />
      </div></Panel>
    )
  }

  const inputClass = (key: keyof IdentityForm) =>
    `mt-1 block w-full bg-ink-900 border px-2 py-1 font-mono text-[12px] text-paper
     ${errors[key] ? 'border-radar-red' : 'border-ink-500'}`
  const fieldError = (key: keyof IdentityForm) => errors[key] && (
    <span className="font-mono text-[10px] text-radar-red">{errors[key]}</span>
  )
  const select = (
    key: 'state' | 'country', label: string, options: [string, string][], blank: string,
  ) => (
    <label className="block">
      <span className="label">{label}</span>
      <select
        value={form[key]}
        onChange={(ev) => {
          const value = ev.target.value
          // A US state code means nothing abroad and a region typed for
          // abroad isn't a US code — moving across the border starts over.
          const crossed = key === 'country' && (value === 'US') !== (form.country === 'US')
          setForm({ ...form, [key]: value, ...(crossed ? { state: '' } : {}) })
        }}
        className={inputClass(key)}
      >
        <option value="">{blank}</option>
        {options.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
      </select>
      {fieldError(key)}
    </label>
  )
  const isUS = form.country === 'US'

  const field = (key: keyof IdentityForm, label: string, placeholder?: string) => (
    <label className="block">
      <span className="label">{label}</span>
      <input
        type="text"
        value={form[key]}
        placeholder={placeholder}
        onChange={(ev) => setForm({ ...form, [key]: ev.target.value })}
        className={inputClass(key)}
      />
      {fieldError(key)}
    </label>
  )

  return (
    <Panel label="Contact" right={right}><div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {field('first_name', 'First name')}
        {field('last_name', 'Last name')}
      </div>
      {field('email', 'Email')}
      {field('address_1', 'Address 1')}
      {field('address_2', 'Address 2')}
      <div className="grid grid-cols-2 gap-2">
        {field('city', 'City')}
        {isUS ? select('state', 'State', US_STATES, 'Choose a state') : field('state', 'State / region')}
        {field('zipcode', isUS ? 'ZIP' : 'Postal code')}
        {select('country', 'Country', COUNTRIES, sub.country && !form.country
          ? `Choose (was "${sub.country}")` : 'Choose a country')}
      </div>
      {field('note', 'Note (optional)', 'e.g. member called to correct')}
      <div className="flex gap-2 pt-1">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
      <div className="font-mono text-[10px] text-paper-trace">
        An address change clears any confirmed print destination; the member re-confirms it.
      </div>
      {failure && <div className="font-mono text-[11px] text-radar-red">{failure}</div>}
      {conflicts.length > 0 && (
        <ul className="font-mono text-[11px] text-radar-red space-y-0.5">
          {conflicts.map((c) => (
            <li key={c.id}>
              also used by <Link className="underline" to={`/subscribers/${c.id}`}>#{c.id}{c.name ? ` ${c.name}` : ''}</Link>
              {' '}— {c.titles.join(', ')}
            </li>
          ))}
        </ul>
      )}
    </div></Panel>
  )
}

function KV({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="label shrink-0">{k}</span>
      <span className={`text-right truncate ${mono ? 'font-mono text-[12px] tabular-nums text-paper-dim' : 'text-[13px] text-paper'}`}>
        {v || <span className="text-paper-trace">—</span>}
      </span>
    </div>
  )
}

function Th({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={`label py-2 px-4 font-normal text-${align}`}>{children}</th>
  )
}

function Td({
  children, mono, subtle, align = 'left',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  align?: 'left' | 'right'
}) {
  return (
    <td
      className={`py-2.5 px-4 text-${align}
        ${mono ? 'font-mono text-[12px] tabular-nums' : 'text-[13px]'}
        ${subtle ? 'text-paper-dim' : 'text-paper'}`}
    >
      {children}
    </td>
  )
}

function RawSimpleCircPanels({ subscriptions }: { subscriptions: Subscription[] }) {
  const withRaw = subscriptions.filter((s) => s.raw_csv_row && Object.keys(s.raw_csv_row).length > 0)
  if (withRaw.length === 0) return null

  return (
    <section className="space-y-3">
      <div className="label">Raw SimpleCirc Data · Last CSV Import</div>
      {withRaw.map((s) => (
        <RawSubscriptionPanel key={s.id} subscription={s} />
      ))}
    </section>
  )
}

function RawSubscriptionPanel({ subscription }: { subscription: Subscription }) {
  const [open, setOpen] = useState(false)
  const row = subscription.raw_csv_row ?? {}
  const entries = Object.entries(row).sort(([a], [b]) => a.localeCompare(b))

  return (
    <Panel
      accent="violet"
      label={`${subscription.publication_name} · ${subscription.simplecirc_subscription_id}`}
      right={
        <button
          onClick={() => setOpen(!open)}
          className="font-mono text-[10px] uppercase tracking-label text-amber-glow hover:underline"
        >
          {open ? '▾ hide' : '▸ show all ' + entries.length + ' fields'}
        </button>
      }
      bodyClassName={open ? 'p-4' : 'p-0'}
    >
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-3 border-b border-ink-500/30 pb-1">
              <span className="font-mono text-[10px] uppercase tracking-label text-paper-faint shrink-0 max-w-[50%]">
                {k}
              </span>
              <span className="font-mono text-[11px] text-paper-dim text-right truncate" title={v}>
                {v}
              </span>
            </div>
          ))}
        </div>
      )}
      {subscription.raw_csv_imported_at && (
        <div className="mt-3 text-right">
          <span className="label text-paper-trace">
            imported {fmtDateTime(subscription.raw_csv_imported_at)}
          </span>
        </div>
      )}
    </Panel>
  )
}

function Flag({ tone, children }: { tone: 'amber' | 'cyan'; children: React.ReactNode }) {
  return (
    <span
      className={`font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border ${
        tone === 'amber'
          ? 'text-amber-glow border-amber-glow/40'
          : 'text-radar-cyan border-radar-cyan/40'
      }`}
    >
      {children}
    </span>
  )
}


function EmailHistoryPanel({ subscriberId }: { subscriberId: number }) {
  const { data } = useQuery({
    queryKey: ['email-log', 'subscriber', subscriberId],
    queryFn: () =>
      api.get<EmailLogPage>(`/api/email-log?subscriber_id=${subscriberId}&limit=10`),
  })

  const items = data?.items ?? []

  return (
    <Panel label="Email History" bodyClassName="p-0" accent="cyan">
      {items.length === 0 && (
        <div className="px-4 py-6 text-center label text-paper-trace">
          No emails recorded
        </div>
      )}
      {items.length > 0 && (
        <table className="w-full">
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b border-ink-600">
                <td className="px-4 py-2 font-mono text-[12px] text-paper-trace">
                  {fmtDateTime(e.occurred_at)}
                </td>
                <td className="px-4 py-2 font-mono text-[12px] text-paper">
                  {e.subject ?? e.template_slug ?? '—'}
                </td>
                <td className="px-4 py-2 font-mono text-[12px] text-paper">
                  {e.status}
                  {e.status_detail && (
                    <span className="text-paper-trace"> · {e.status_detail}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="px-4 py-3">
        <Link
          to={`/emails?subscriber_id=${subscriberId}`}
          className="label text-amber-glow"
        >
          Full history →
        </Link>
      </div>
    </Panel>
  )
}
